import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import jwt_decode from 'jwt-decode';
import { Outlet, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const AuthContext = createContext();

export default AuthContext;

function getTokens() {
    const raw = localStorage.getItem('authTokens');
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        localStorage.removeItem('authTokens');
        return null;
    }
}

function getUserFromTokens(tokens) {
    if (!tokens?.access) return null;
    try {
        const data = jwt_decode(tokens.access);
        return { username: data.username };
    } catch {
        return null;
    }
}

// Seconds until the access token expires (negative = already expired).
function secondsLeft(tokens) {
    if (!tokens?.access) return -1;
    try {
        return jwt_decode(tokens.access).exp - Date.now() / 1000;
    } catch {
        return -1;
    }
}

// Refresh this close to expiry. Access tokens live 5 days, refresh tokens 90;
// without this the browser kept saying "logged in" while every request 401'd.
const REFRESH_AHEAD_SECONDS = 10 * 60;

export function AuthProvider() {

    const [authTokens, setAuthTokens] = useState(() => getTokens());
    const [user, setUser] = useState(() => getUserFromTokens(getTokens()));
    // Hold the app until a stale access token has been refreshed (or dropped),
    // so pages never fire their first requests with a token the server rejects.
    const [ready, setReady] = useState(() => secondsLeft(getTokens()) > 0 || !getTokens());
    const navigate = useNavigate();
    const refreshing = useRef(null);

    function persistTokens(tokens) {
        setAuthTokens(tokens);
        setUser(getUserFromTokens(tokens));
        localStorage.setItem('authTokens', JSON.stringify(tokens));
    }

    const clearSession = useCallback(() => {
        setAuthTokens(null);
        setUser(null);
        localStorage.removeItem('authTokens');
    }, []);

    // Exchange the refresh token for a new pair. Deduped: concurrent callers
    // share one request. Returns the new tokens, or null if the session is over.
    const refreshTokens = useCallback(async () => {
        if (refreshing.current) return refreshing.current;
        const current = getTokens();
        if (!current?.refresh) { clearSession(); return null; }
        refreshing.current = (async () => {
            try {
                const r = await axios.post(`${API_BASE_URL}/api/users/token/refresh/`, { refresh: current.refresh });
                // Rotation returns a new refresh token too; keep the old one only if it didn't.
                const next = { ...current, ...r.data, refresh: r.data.refresh || current.refresh };
                persistTokens(next);
                return next;
            } catch (err) {
                if (err.response && [400, 401, 403].includes(err.response.status)) {
                    clearSession();   // refresh token expired or blacklisted: truly logged out
                    return null;
                }
                return current;       // network blip: keep what we have, try again later
            } finally {
                refreshing.current = null;
            }
        })();
        return refreshing.current;
    }, [clearSession]);

    // Refresh when close to expiry: on load, when the tab regains focus, and
    // once a minute while open.
    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            const t = getTokens();
            if (t?.access && secondsLeft(t) < REFRESH_AHEAD_SECONDS) await refreshTokens();
            if (!cancelled) setReady(true);
        };
        check();
        const onFocus = () => { check(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        const timer = setInterval(check, 60 * 1000);
        return () => {
            cancelled = true;
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
            clearInterval(timer);
        };
    }, [refreshTokens]);

    // "Don't ask again on this device": a per-browser token minted after a 2FA
    // code was accepted. Kept across logouts on purpose; the server forgets it
    // after 30 days or when the user revokes it from their profile.
    const DEVICE_KEY = 'rawiyaih_device';
    const getDeviceToken = () => { try { return localStorage.getItem(DEVICE_KEY) || ''; } catch { return ''; } };

    async function loginUser(data) {
        try {
            const deviceToken = getDeviceToken();
            const response = await axios.post(`${API_BASE_URL}/api/users/token/`, {
                username: data.username,
                password: data.password,
                ...(deviceToken ? { device_token: deviceToken } : {}),
            });
            // 200 with a 2fa_required code = no tokens yet; caller must verify the code.
            if (response.status === 200 && response.data?.access) {
                persistTokens(response.data);
            } else if (response.status === 200 && response.data?.code === '2fa_required' && deviceToken) {
                try { localStorage.removeItem(DEVICE_KEY); } catch { /* ignore */ }   // expired or revoked
            }
            return response;
        } catch (err) {
            return err.response ?? err;
        }
    }

    async function verifyTwoFactor(preAuthToken, code, rememberDevice = false) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/users/2fa/verify/`, {
                pre_auth_token: preAuthToken,
                code,
                remember_device: !!rememberDevice,
            });
            if (response.status === 200 && response.data?.access) {
                persistTokens(response.data);
                if (response.data.device_token) {
                    try { localStorage.setItem(DEVICE_KEY, response.data.device_token); } catch { /* ignore */ }
                }
            }
            return response;
        } catch (err) {
            return err.response ?? err;
        }
    }

    async function resendTwoFactor(preAuthToken) {
        try {
            return await axios.post(`${API_BASE_URL}/api/users/2fa/resend/`, {
                pre_auth_token: preAuthToken,
            });
        } catch (err) {
            return err.response ?? err;
        }
    }

    async function googleLogin(credential) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/users/google-auth/`, { credential });
            if (response.status === 200) {
                persistTokens(response.data);
            }
            return response;
        } catch (err) {
            return err.response ?? err;
        }
    }

    function acceptTokens(tokens) {
        // For flows that already have JWTs (e.g. email-verify success).
        persistTokens(tokens);
    }

    function logoutUser() {
        clearSession();
        navigate('/');
    }

    const contextData = {
        user,
        setUser,
        authTokens,
        setAuthTokens,
        loginUser,
        googleLogin,
        verifyTwoFactor,
        resendTwoFactor,
        acceptTokens,
        logoutUser,
        refreshTokens,
    };

    return (
        <AuthContext.Provider value={contextData}>
            {ready ? <Outlet /> : null}
        </AuthContext.Provider>
    );
}